/* eslint-disable no-nested-ternary */
import * as vp from 'vopay';
import * as Yup from 'yup';
import { useContext, useMemo } from 'react';
import styles from './useAccountCreationFields.module.scss';

import {
  createEmailField,
  createSelectField,
  createTextField,
  createToggleField,
  createTextGoogleAddressFeild,
  validationMessages,
  createNumberFieldLead,
  createCheckboxField,
} from './useFields.helpers';

import fieldUtilsFactory from './fieldUtils.factory';

import type { NewAccountData } from '../../components/Accounts/types';
import type { FieldUtils, SubSection } from './types';
import type { MultiStepFormCtx } from '../../providers/MultiStepForm.provider';
import { useClientData } from '../../providers/ClientData.provider';
import InteracInfoContext from '../../components/Accounts/AccountCreation/Interac/InteracInfoContext';
import { Button } from 'vopay';

export type AccountCreationSections = {
  contactSettings: SubSection<NewAccountData>[];
  contactDetails: SubSection<NewAccountData>[];
  accountDetails: SubSection<NewAccountData>[];
  accountDetailsEtransfer: SubSection<NewAccountData>[];
  tokenDetails: SubSection<NewAccountData>[];
  provideDetailsBankAccount: SubSection<NewAccountData>[];
  contactDetailsSecondBank: SubSection<NewAccountData>[];
  connectionRequest: SubSection<NewAccountData>[];
};

export type AccountCreationSection = keyof AccountCreationSections;
export type Ctx = MultiStepFormCtx<NewAccountData>;
const useAccountCreationFields = (): FieldUtils<NewAccountData> => {
  const InteracCtx = useContext(InteracInfoContext);

  const {
    response: { ClientAccountsEnabled, KYCLightEnabled, Country },
  } = useClientData();

  const hasRecipientName = InteracCtx.interacDetails?.RecipientName !== '';
  const hasEmailAddress = InteracCtx.interacDetails?.RecipientEmailAddress !== '';
  const hasPhoneNumber = InteracCtx.interacDetails?.RecipientPhoneNumber !== '';
  const hasQuestion = InteracCtx.interacDetails?.Question !== '';
  const hasAnswer = InteracCtx.interacDetails?.Answer !== '';

  const sections: AccountCreationSections = useMemo(
    () => ({
      contactSettings: [
        {
          label: 'Contact Settings',
          fields: {
            paymentMethod: {
              label: 'What type of account is being connected?',
              placeholder: 'Payment Method',
              fieldName: 'paymentMethod',
              initValue: 'bank',
              dataType: 'mapped',
              getInputState: () => 'required',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createSelectField(this, ctx, { noOrdering: true });
              },
              getSelectOptions: () =>
                Country === 'US'
                  ? {
                      bank: 'Bank Account',
                      creditCard: 'Credit Card',
                    }
                  : vp.PAYMENT_METHOD_OPTIONS,
              validate() {
                return Yup.string().required('This value is required.');
              },
            },
            accountType: {
              label: 'Bank Account Type',
              placeholder: 'Bank Account Type',
              fieldName: 'accountType',
              initValue: 'Business',
              dataType: 'mapped',
              getInputState: (ctx: Ctx) => (ctx.values.paymentMethod === 'bank' ? 'required' : 'optional'),
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createSelectField(this, ctx, { noOrdering: true });
              },
              getSelectOptions: () => vp.ACCOUNT_TYPE_OPTIONS,
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            currency: {
              label: 'Account Currency',
              placeholder: 'Account Currency',
              fieldName: 'currency',
              initValue: Country === 'US' ? 'USD' : 'CAD',
              dataType: 'mapped',
              getInputState: () => 'required',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createSelectField(this, ctx, { noOrdering: true });
              },

              getSelectOptions: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' ? vp.CURRENCY_LIST_OPTIONS : vp.CA_CURRENCY_ONLY,
            },
            country: {
              label: 'Account Country',
              placeholder: 'Bank Account Country',
              fieldName: 'country',
              initValue: Country === 'US' ? 'US' : 'CA',
              dataType: 'mapped',
              showField: () => true,
              getInputState: () => 'required',
              getElement(ctx: Ctx) {
                return createSelectField(this, ctx, { noOrdering: true });
              },
              getSelectOptions: (ctx: Ctx) => {
                if (ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') {
                  return ctx.values.currency === 'CAD'
                    ? vp.CANADA_US_COUNTRY_OPTION
                    : ctx.values.currency === 'USD'
                    ? vp.CANADA_US_COUNTRY_OPTION
                    : ctx.values.currency === 'GBP'
                    ? vp.ENGLAND_COUNTRY_OPTION
                    : ctx.values.currency === 'EUR'
                    ? vp.EUROPE_COUNTRY_OPTION
                    : ctx.values.currency === 'AUD'
                    ? vp.AUSTRALIA_COUNTRY_OPTION
                    : ctx.values.currency === 'DKK'
                    ? vp.DENMARK_COUNTRY_OPTION
                    : ctx.values.currency === 'SEK'
                    ? vp.SWEDEN_COUNTRY_OPTION
                    : ctx.values.currency === 'NOK'
                    ? vp.NORWAY_COUNTRY_OPTION
                    : ctx.values.currency === 'PLN'
                    ? vp.POLAND_COUNTRY_OPTION
                    : vp.CANADA_US_COUNTRY_OPTION;
                }

                return vp.CANADA_COUNTRY_OPTION;
              },
              validate() {
                return Yup.string().required('This value is required.');
              },
            },
          },
          showSection: () => true,
        },
      ],
      contactDetails: [
        {
          label: 'Contact Details',
          fields: {
            accountType: {
              label: 'Bank Account Type',
              placeholder: 'Bank Account Type',
              fieldName: 'accountType',
              initValue: 'Business',
              dataType: 'mapped',
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' && ClientAccountsEnabled === '0' ? 'required' : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createSelectField(this, ctx, { noOrdering: true });
              },
              getSelectOptions: () => vp.ACCOUNT_TYPE_OPTIONS,
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            companyName: {
              label: 'Business Name',
              placeholder: 'Business Name',
              fieldName: 'companyName',
              initValue: '',
              dataType: 'text',
              // getInputState: () => 'required',
              getInputState: (ctx: Ctx) =>
                ctx.values.accountType === 'Business' &&
                ctx.values.paymentMethod === 'bank' &&
                ClientAccountsEnabled === '0'
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.accountType === 'Business' && ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            firstName: {
              label: 'First Name',
              placeholder: 'First Name',
              fieldName: 'firstName',
              initValue: '',
              dataType: 'text',
              // getInputState: () => 'required',
              getInputState: (ctx: Ctx) =>
                ctx.values.accountType === 'Individual' &&
                ctx.values.paymentMethod === 'bank' &&
                ClientAccountsEnabled === '0'
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.accountType === 'Individual' && ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            lastName: {
              label: 'Last Name',
              placeholder: 'Last Name',
              fieldName: 'lastName',
              initValue: '',
              dataType: 'text',
              // getInputState: () => 'required',
              getInputState: (ctx: Ctx) =>
                ctx.values.accountType === 'Individual' &&
                ctx.values.paymentMethod === 'bank' &&
                ClientAccountsEnabled === '0'
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.accountType === 'Individual' && ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            RecipientName: {
              label: 'Contact Name',
              placeholder: 'Contact Name',
              fieldName: 'RecipientName',
              initValue: '',
              dataType: 'text',
              // getInputState: () => 'required',
              getInputState: (ctx: Ctx) => (ctx.values.paymentMethod === 'creditCard' ? 'required' : 'optional'),
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'creditCard',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return ctx.values.paymentMethod === 'e-transfer' ||
                  ctx.values.paymentMethod === 'creditCard' ||
                  ((ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') &&
                    ctx.values.connectionType === 'Paylink' &&
                    ClientAccountsEnabled === '1')
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            clientReferenceNumber: {
              label: 'Client Reference Number',
              placeholder: 'Client Reference Number',
              fieldName: 'clientReferenceNumber',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
            },
            nickname: {
              label: 'Nickname',
              placeholder: 'Nickname',
              fieldName: 'nickname',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
            },
            emailAddress: {
              label: 'Email Address',
              placeholder: 'name@domain.ca',
              fieldName: 'emailAddress',
              initValue: '',
              dataType: 'email',
              getInputState: (ctx: Ctx) =>
                (ctx.values.paymentMethod === 'bank' && ClientAccountsEnabled === '0') ||
                ctx.values.paymentMethod === 'creditCard'
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard',
              getElement(ctx: Ctx) {
                return createEmailField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().email(validationMessages.invalidEmail).required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            contactNumber: {
              label: 'Contact Number ',
              placeholder: 'Contact Number',
              fieldName: 'contactNumber',
              initValue: '',
              dataType: 'text',
              getInputState: (ctx: Ctx) =>
                ctx.values.country === 'US' && ctx.values.paymentMethod === 'bank' && !vp.toBool(ClientAccountsEnabled)
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            IdentificationNumber: {
              label: 'Identification Number',
              placeholder: 'Identification Number',
              fieldName: 'IdentificationNumber',
              initValue: '',
              dataType: 'text',
              getInputState: (ctx: Ctx) =>
                ctx.values.country === 'US' && ctx.values.paymentMethod === 'bank' && !vp.toBool(KYCLightEnabled)
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank' && ctx.values.country === 'US',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
          },

          showSection: () => true,
        },
        {
          label: 'Account Address',
          fields: {
            address1: {
              label: 'Address',
              placeholder: 'Address',
              fieldName: 'address1',
              initValue: '',
              dataType: 'text',
              getGoogleCountries: (ctx: Ctx) => ({ [ctx.values.country || 'CA']: ctx.values.country || 'CA' }),
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' && ClientAccountsEnabled === '0' ? 'required' : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              handleGoogleResults: (ctx: Ctx, addressComponents: vp.AddressComponents) => {
                ctx.handleFieldChange(vp.createSyntheticEvent('address1', addressComponents.address));
                ctx.handleFieldChange(vp.createSyntheticEvent('city', addressComponents.city));
                ctx.handleFieldChange(vp.createSyntheticEvent('province', addressComponents.province));
                ctx.handleFieldChange(vp.createSyntheticEvent('country', addressComponents.country));
                ctx.handleFieldChange(vp.createSyntheticEvent('postalCode', addressComponents.postalcode));
              },
              getElement(ctx: Ctx) {
                return createTextGoogleAddressFeild(this, ctx);
              },

              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string()
                      .required('Please enter a address.')
                      .test(
                        'valid-address-values',
                        validationMessages.noSpecialChars,
                        (value) => !/[^\w ,:'()/\-.]|\s{5}/.test(value || ''),
                      )
                  : Yup.string().notRequired();
              },
            },
            address2: {
              label: 'Unit',
              placeholder: 'Unit',
              fieldName: 'address2',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate() {
                return Yup.string().test(
                  'valid-address-values',
                  validationMessages.noSpecialChars,
                  (value) => !/[^\w ,:'()/\-.]|\s{5}/.test(value || ''),
                );
              },
            },
            city: {
              label: 'City',
              placeholder: 'City',
              fieldName: 'city',
              initValue: '',
              dataType: 'text',
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' && ClientAccountsEnabled === '0' ? 'required' : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('Please select a city.')
                  : Yup.string().notRequired();
              },
            },
            province: {
              label: 'Province/State',
              placeholder: 'Province/State',
              fieldName: 'province',
              initValue: '',
              dataType: 'text',
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' && ClientAccountsEnabled === '0' ? 'required' : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('Please enter a province or state.')
                  : Yup.string().notRequired();
              },
            },
            postalCode: {
              label: 'Postal Code/Zip Code',
              placeholder: 'Postal Code/Zip Code',
              fieldName: 'postalCode',
              initValue: '',
              dataType: 'text',
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' && ClientAccountsEnabled === '0' ? 'required' : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('Please enter a postal code.')
                  : Yup.string().notRequired();
              },
            },
          },
          showSection: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
        },
      ],
      accountDetails: [
        {
          label: 'Account Details',
          fields: {
            connectionType: {
              label: 'How would you like to connect this account?',
              placeholder: 'Connection Type',
              fieldName: 'connectionType',
              initValue: 'Manual',
              dataType: 'mapped',
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard'
                  ? 'required'
                  : 'optional',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createSelectField(this, ctx, { noOrdering: true });
              },
              getSelectOptions: () => vp.ACCOUNT_CONNECTION_OPTIONS,
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
          },
          showSection: () => true,
        },
      ],
      accountDetailsEtransfer: [
        {
          label: 'Account Details',
          fields: {
            etransferConnectionType: {
              label: 'How will you send e-Transfers to this contact?',
              placeholder: 'Connection Type',
              fieldName: 'etransferConnectionType',
              initValue: 'email',
              dataType: 'mapped',
              getInputState(ctx: Ctx) {
                if (
                  ctx.values.paymentMethod === 'e-transfer' &&
                  ClientAccountsEnabled === '1' &&
                  !InteracCtx.addInteracEmail
                ) {
                  return 'disabled';
                }

                if (
                  ctx.values.paymentMethod === 'e-transfer' &&
                  ClientAccountsEnabled === '0' &&
                  InteracCtx.addInteracEmail
                ) {
                  return 'required';
                }

                return 'optional';
              },
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'e-transfer',
              getElement(ctx: Ctx) {
                return createSelectField(this, ctx, { noOrdering: true });
              },
              getSelectOptions: () => vp.ETRANSFER_CONNECTION_OPTIONS,
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            RecipientName: {
              label: 'Recipient Name',
              placeholder: 'Recipient Name',
              fieldName: 'RecipientName',
              initValue: '',
              dataType: 'text',
              getInputState(ctx: Ctx) {
                if (hasRecipientName && vp.toBool(ClientAccountsEnabled)) {
                  return 'disabled';
                }
                if (ctx.values.paymentMethod === 'e-transfer') {
                  return 'required';
                }
                return 'optional';
              },
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'e-transfer',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return ctx.values.paymentMethod === 'e-transfer' ||
                  ctx.values.paymentMethod === 'creditCard' ||
                  ((ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') &&
                    ctx.values.connectionType === 'Paylink' &&
                    ClientAccountsEnabled === '1')
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            RecipientPhoneNumber: {
              label: 'Phone Number',
              placeholder: '+1 604-111-1111',
              fieldName: 'RecipientPhoneNumber',
              initValue: '',
              dataType: 'text',
              getInputState(ctx: Ctx) {
                if (ctx.values.etransferConnectionType === 'phone') {
                  if (hasPhoneNumber && vp.toBool(ClientAccountsEnabled)) {
                    return 'disabled';
                  }
                  if (ctx.values.paymentMethod === 'e-transfer') {
                    return 'required';
                  }
                }
                return 'optional';
              },
              showField: (ctx: Ctx) => ctx.values.etransferConnectionType === 'phone',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            RecipientEmailAddress: {
              label: 'Email Address',
              placeholder: 'name@domain.ca',
              fieldName: 'RecipientEmailAddress',
              initValue: '',
              dataType: 'email',
              getInputState(ctx: Ctx) {
                if (ctx.values.etransferConnectionType === 'email') {
                  if (hasEmailAddress && vp.toBool(ClientAccountsEnabled)) {
                    return 'disabled';
                  }
                  if (ctx.values.paymentMethod === 'e-transfer') {
                    return 'required';
                  }
                }
                return 'optional';
              },
              showField: (ctx: Ctx) => ctx.values.etransferConnectionType === 'email',
              getElement(ctx: Ctx) {
                return createEmailField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().email(validationMessages.invalidEmail).required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            Question: {
              label: 'Security Question',
              placeholder: 'Type a security question here',
              fieldName: 'Question',
              initValue: '',
              dataType: 'text',
              getInputState(ctx: Ctx) {
                if (hasQuestion && vp.toBool(ClientAccountsEnabled)) {
                  return 'disabled';
                }
                if (InteracCtx.isInteracQuestionsRequired && ctx.values.paymentMethod === 'e-transfer') {
                  return 'required';
                }
                return 'optional';
              },
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'e-transfer',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return InteracCtx.isInteracQuestionsRequired && ctx.values.paymentMethod === 'e-transfer'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            Answer: {
              label: 'Security Answer',
              placeholder: 'Type an answer here',
              fieldName: 'Answer',
              initValue: '',
              dataType: 'text',
              getInputState(ctx: Ctx) {
                if (hasAnswer && vp.toBool(ClientAccountsEnabled)) {
                  return 'disabled';
                }
                if (InteracCtx.isInteracQuestionsRequired && ctx.values.paymentMethod === 'e-transfer') {
                  return 'required';
                }
                return 'optional';
              },
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'e-transfer',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return InteracCtx.isInteracQuestionsRequired && ctx.values.paymentMethod === 'e-transfer'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
          },
          showSection: (ctx: Ctx) => ctx.values.paymentMethod === 'e-transfer',
        },
      ],
      tokenDetails: [
        {
          label: 'Token Details',
          fields: {
            Token: {
              label: 'Connection Token',
              placeholder: 'Connection Token',
              fieldName: 'Token',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: () => false,
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
            },
          },
          showSection: () => true,
        },
      ],
      provideDetailsBankAccount: [
        {
          label: 'Provide Details',
          fields: {
            FinancialInstitutionNumber: {
              label: 'Financial Institution Number',
              placeholder: ' Input Financial Institution Number',
              fieldName: 'FinancialInstitutionNumber',
              initValue: '',
              dataType: 'number',
              getInputState: (ctx: Ctx) =>
                ctx.values.country === 'CA' &&
                ctx.values.paymentMethod === 'bank' &&
                !ctx.values.isClientControlled &&
                ctx.values.connectionType === 'Manual'
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.country === 'CA',

              getSelectOptions(ctx: Ctx) {
                return ctx.values.FinancialInstitutions || {};
              },
              getElement(ctx: Ctx) {
                if (!ctx.values.financialManual) {
                  return createSelectField(this, ctx, {
                    noOrdering: false,
                    placeholderColor: '#d8d8d8',
                    orderByValues: true,
                    showValue: true,
                    renderDropdownHeader: () => (
                      <div className={styles.dropdownHeader}>
                        <div>
                          <Button
                            color="secondary"
                            label="Enter Financial Institution Manually"
                            onClick={() => {
                              ctx.setFieldValue('financialManual', true);
                              ctx.setFieldValue('branchTransitManual', true);
                              ctx.setFieldValue('FinancialInstitutionNumber', '');
                              ctx.setFieldValue('BranchTransitNumber', '');
                              ctx.setFieldTouched('BranchTransitNumber', false);
                              ctx.setFieldTouched('FinancialInstitutionNumber', false);
                              setTimeout(() => {
                                (
                                  document.querySelector('input[name="FinancialInstitutionNumber"]') as HTMLInputElement
                                )?.focus();
                              });
                            }}
                            type="button"
                          />
                        </div>
                      </div>
                    ),
                  });
                } else {
                  return createTextField(this, ctx);
                }
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string()
                      .required('This value is required.')
                      .max(3, 'Financial Institution Number must 3 or less digits')
                  : Yup.string().notRequired();
              },
            },
            BranchTransitNumber: {
              label: 'Branch/Transit Number',
              placeholder: 'Input Branch/Transit Number',
              fieldName: 'BranchTransitNumber',
              initValue: '',
              dataType: 'number',

              getInputState: (ctx: Ctx) =>
                ctx.values.country === 'CA' &&
                ctx.values.paymentMethod === 'bank' &&
                !ctx.values.isClientControlled &&
                ctx.values.connectionType === 'Manual'
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.country === 'CA',
              getSelectOptions(ctx: Ctx) {
                return ctx.values.BranchTransitNumbers || {};
              },

              getElement(ctx: Ctx) {
                if (!ctx.values.branchTransitManual) {
                  return createSelectField(this, ctx, {
                    noOrdering: false,
                    placeholderColor: '#d8d8d8',
                    orderByValues: true,
                    showValue: true,
                    renderDropdownHeader: () => (
                      <div className={styles.dropdownHeader}>
                        <div>
                          <Button
                            color="secondary"
                            label=" Enter Branch Transit Number Manually"
                            onClick={() => {
                              ctx.setFieldValue('branchTransitManual', true);
                              ctx.setFieldValue('BranchTransitNumber', '');
                              ctx.setFieldTouched('BranchTransitNumber', false);
                              setTimeout(() => {
                                (
                                  document.querySelector('input[name="BranchTransitNumber"]') as HTMLInputElement
                                )?.focus();
                              });
                            }}
                            type="button"
                          />
                        </div>
                      </div>
                    ),
                  });
                } else {
                  return createTextField(this, ctx);
                }
              },

              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string()
                      .required('This value is required.')
                      .max(5, 'Branch/Transit Number must 5 or less digits')
                  : Yup.string().notRequired();
              },
            },
            ABARoutingNumber: {
              label: 'ABA Routing Number',
              placeholder: 'Input ABA Routing Number',
              fieldName: 'ABARoutingNumber',
              initValue: '',
              dataType: 'number',
              getInputState: (ctx: Ctx) =>
                ctx.values.country === 'US' &&
                ctx.values.paymentMethod === 'bank' &&
                ctx.values.connectionType === 'Manual' &&
                !ctx.values.isClientControlled
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.country === 'US',
              getElement(ctx: Ctx) {
                return createNumberFieldLead(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string()
                      .required('This value is required.')
                      .test('aba', 'Enter a valid ABA Routing Number', (value) => {
                        const match = /^\s*([\d]{9})\s*$/.exec(value || '');
                        if (!match) {
                          return false;
                        }

                        const weights = [3, 7, 1];
                        const aba = match[1];

                        let sum = 0;
                        // eslint-disable-next-line no-plusplus
                        for (let i = 0; i < 9; ++i) {
                          // using charAt for IE7 support
                          sum += Number(aba.charAt(i)) * weights[i % 3];
                        }

                        return sum !== 0 && sum % 10 === 0;
                      })
                  : Yup.string().notRequired();
              },
            },
            AccountNumber: {
              label: 'Account Number',
              placeholder: 'Input Account Number',
              fieldName: 'AccountNumber',
              initValue: '',
              dataType: 'number',
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' &&
                ctx.values.connectionType === 'Manual' &&
                !ctx.values.isClientControlled
                  ? 'required'
                  : 'optional',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createNumberFieldLead(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },

            isClientControlled: {
              label: 'isClientControlled',
              fieldName: 'isClientControlled',
              initValue: true,
              dataType: 'boolean',
              showField: () => false,
              getInputState: () => 'optional',
              getElement(ctx: Ctx) {
                return createToggleField(this, ctx);
              },
              validate() {
                Yup.string().notRequired();
              },
            },

            saveAccountInformation: {
              label: 'Save Account Infomation',
              fieldName: 'saveAccountInformation',
              initValue: true,
              dataType: 'boolean',
              showField: (ctx: Ctx) => ctx.values.showSave,
              getInputState: () => 'optional',
              getElement(ctx: Ctx) {
                return createToggleField(this, ctx);
              },
              validate() {
                Yup.string().notRequired();
              },
            },
          },
          showSection: () => true,
        },
      ],
      contactDetailsSecondBank: [
        {
          label: 'Contact Details',
          fields: {
            accountTypeSecond: {
              label: 'Bank Account Type',
              placeholder: 'Bank Account Type',
              fieldName: 'accountTypeSecond',
              initValue: 'Business',
              dataType: 'mapped',
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' && ctx.values.isAllow2Banks ? 'required' : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank' && ctx.values.isAllow2Banks,
              getElement(ctx: Ctx) {
                return createSelectField(this, ctx, { noOrdering: true });
              },
              getSelectOptions: () => vp.ACCOUNT_TYPE_OPTIONS,
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            companyNameSecond: {
              label: 'Business Name',
              placeholder: 'Business Name',
              fieldName: 'companyNameSecond',
              initValue: '',
              dataType: 'text',
              // getInputState: () => 'required',
              getInputState: (ctx: Ctx) =>
                ctx.values.accountType === 'Business' && ctx.values.paymentMethod === 'bank' && ctx.values.isAllow2Banks
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) =>
                ctx.values.accountType === 'Business' &&
                ctx.values.paymentMethod === 'bank' &&
                ctx.values.isAllow2Banks,
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            firstNameSecond: {
              label: 'First Name',
              placeholder: 'First Name',
              fieldName: 'firstNameSecond',
              initValue: '',
              dataType: 'text',
              // getInputState: () => 'required',
              getInputState: (ctx: Ctx) =>
                ctx.values.accountType === 'Individual' &&
                ctx.values.paymentMethod === 'bank' &&
                ctx.values.isAllow2Banks
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.accountType === 'Individual' && ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            lastNameSecond: {
              label: 'Last Name',
              placeholder: 'Last Name',
              fieldName: 'lastNameSecond',
              initValue: '',
              dataType: 'text',
              // getInputState: () => 'required',
              getInputState: (ctx: Ctx) =>
                ctx.values.accountType === 'Individual' &&
                ctx.values.paymentMethod === 'bank' &&
                ctx.values.isAllow2Banks
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.accountType === 'Individual' && ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            RecipientNameSecond: {
              label: 'Contact Name',
              placeholder: 'Contact Name',
              fieldName: 'RecipientNameSecond',
              initValue: '',
              dataType: 'text',
              // getInputState: () => 'required',
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod !== 'bank' && ctx.values.isAllow2Banks ? 'required' : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod !== 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            clientReferenceNumberSecond: {
              label: 'Client Reference Number',
              placeholder: 'Client Reference Number',
              fieldName: 'clientReferenceNumberSecond',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
            },
            nicknameSecond: {
              label: 'Nickname',
              placeholder: 'Nickname',
              fieldName: 'nicknameSecond',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
            },
            emailAddressSecond: {
              label: 'Email Address',
              placeholder: 'name@domain.ca',
              fieldName: 'emailAddressSecond',
              initValue: '',
              dataType: 'email',
              getInputState: (ctx: Ctx) =>
                (ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') &&
                ctx.values.isAllow2Banks
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard',
              getElement(ctx: Ctx) {
                return createEmailField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().email(validationMessages.invalidEmail).required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            contactNumberSecond: {
              label: 'Contact Number ',
              placeholder: 'Contact Number',
              fieldName: 'contactNumberSecond',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
            },
          },
          showSection: () => true,
        },
        {
          label: 'Account Address',
          fields: {
            address1Second: {
              label: 'Address',
              placeholder: 'Address',
              fieldName: 'address1Second',
              initValue: '',
              dataType: 'text',
              getGoogleCountries: (ctx: Ctx) => ({ [ctx.values.country || 'CA']: ctx.values.country || 'CA' }),
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' && ctx.values.isAllow2Banks ? 'required' : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              handleGoogleResults: (ctx: Ctx, addressComponents: vp.AddressComponents) => {
                ctx.handleFieldChange(vp.createSyntheticEvent('address1Second', addressComponents.address));
                ctx.handleFieldChange(vp.createSyntheticEvent('citySecond', addressComponents.city));
                ctx.handleFieldChange(vp.createSyntheticEvent('provinceSecond', addressComponents.province));
                ctx.handleFieldChange(vp.createSyntheticEvent('countrySecond', addressComponents.country));
                ctx.handleFieldChange(vp.createSyntheticEvent('postalCodeSecond', addressComponents.postalcode));
              },
              getElement(ctx: Ctx) {
                return createTextGoogleAddressFeild(this, ctx);
              },

              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string()
                      .required('Please enter a address.')
                      .test(
                        'valid-address-values',
                        validationMessages.noSpecialChars,
                        (value) => !/[^\w ,:'()/\-.]|\s{5}/.test(value || ''),
                      )
                  : Yup.string().notRequired();
              },
            },
            address2Second: {
              label: 'Unit',
              placeholder: 'Unit',
              fieldName: 'address2Second',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate() {
                return Yup.string().test(
                  'valid-address-values',
                  validationMessages.noSpecialChars,
                  (value) => !/[^\w ,:'()/\-.]|\s{5}/.test(value || ''),
                );
              },
            },
            citySecond: {
              label: 'City',
              placeholder: 'City',
              fieldName: 'citySecond',
              initValue: '',
              dataType: 'text',
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' && ctx.values.isAllow2Banks ? 'required' : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('Please select a city.')
                  : Yup.string().notRequired();
              },
            },
            provinceSecond: {
              label: 'Province/State',
              placeholder: 'Province/State',
              fieldName: 'provinceSecond',
              initValue: '',
              dataType: 'text',
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' && ctx.values.isAllow2Banks ? 'required' : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('Please enter a province or state.')
                  : Yup.string().notRequired();
              },
            },
            postalCodeSecond: {
              label: 'Postal Code/Zip Code',
              placeholder: 'Postal Code/Zip Code',
              fieldName: 'postalCodeSecond',
              initValue: '',
              dataType: 'text',
              getInputState: (ctx: Ctx) =>
                ctx.values.paymentMethod === 'bank' && ctx.values.isAllow2Banks ? 'required' : 'optional',
              showField: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('Please enter a postal code.')
                  : Yup.string().notRequired();
              },
            },
          },
          showSection: (ctx: Ctx) => ctx.values.paymentMethod === 'bank',
        },
      ],

      connectionRequest: [
        {
          label: 'Contact Details',
          fields: {
            paylinkEmailAddress: {
              label: 'Recipient Email Address',
              placeholder: 'someone@example.com',
              fieldName: 'paylinkEmailAddress',
              initValue: '',
              dataType: 'email',
              getInputState: (ctx: Ctx) =>
                (ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') &&
                ctx.values.connectionType === 'Paylink' &&
                ctx.values.country === 'CA'
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) =>
                !!(
                  (ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') &&
                  ctx.values.connectionType === 'Paylink' &&
                  ctx.values.country === 'CA'
                ),
              getElement(ctx: Ctx) {
                return createEmailField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().email(validationMessages.invalidEmail).required(validationMessages.missingEmail)
                  : Yup.string().notRequired();
              },
            },
            RecipientName: {
              label: 'Recipient Name',
              placeholder: 'Recipient Name',
              fieldName: 'RecipientName',
              initValue: '',
              dataType: 'text',
              getInputState() {
                if (ClientAccountsEnabled === '1') {
                  return 'required';
                }
                return 'optional';
              },
              showField: () => ClientAccountsEnabled === '1',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return ctx.values.paymentMethod === 'e-transfer' ||
                  ctx.values.paymentMethod === 'creditCard' ||
                  ((ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') &&
                    ctx.values.connectionType === 'Paylink' &&
                    ClientAccountsEnabled === '1')
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
          },
          showSection: () => true,
        },
        {
          label: 'eLinx Details',
          fields: {
            senderName: {
              label: 'Sent By',
              placeholder: 'Enter Sender Name',
              fieldName: 'senderName',
              initValue: '',
              dataType: 'text',
              // getInputState: () => 'required',
              getInputState: (ctx: Ctx) =>
                (ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') &&
                ctx.values.connectionType === 'Paylink' &&
                ctx.values.country === 'CA'
                  ? 'required'
                  : 'optional',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            accountSelectionMethod: {
              label: 'Account Selection Method',
              placeholder: 'Select a Method',
              fieldName: 'accountSelectionMethod',
              initValue: 'any',
              dataType: 'mapped',
              getInputState: (ctx: Ctx) =>
                (ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') &&
                ctx.values.connectionType === 'Paylink' &&
                ctx.values.country === 'CA'
                  ? 'required'
                  : 'optional',
              showField: (ctx: Ctx) =>
                !!(
                  (ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') &&
                  ctx.values.connectionType === 'Paylink' &&
                  ctx.values.country === 'CA'
                ),
              getElement(ctx: Ctx) {
                return createSelectField(this, ctx);
              },
              getSelectOptions: () => vp.ACCOUNT_SELECTION_METHOD_OPTIONS,
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
            },
            language: {
              label: 'Language',
              placeholder: 'Select a Language',
              fieldName: 'language',
              initValue: '',
              dataType: 'mapped',
              getInputState: (ctx: Ctx) =>
                (ctx.values.paymentMethod === 'bank' || ctx.values.paymentMethod === 'creditCard') &&
                ctx.values.connectionType === 'Paylink' &&
                ctx.values.country === 'CA'
                  ? 'required'
                  : 'optional',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createSelectField(this, ctx);
              },
              validate(ctx: Ctx) {
                return this.getInputState(ctx) === 'required'
                  ? Yup.string().required('This value is required.')
                  : Yup.string().notRequired();
              },
              getSelectOptions: () => vp.LANGUAGE_SELECTION_OPTIONS,
            },
            note: {
              label: 'Message to Contact',
              placeholder: 'Enter message here...',
              fieldName: 'note',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
            },
            invoiceNumber: {
              label: 'Invoice Number',
              placeholder: 'Invoice Number',
              fieldName: 'invoiceNumber',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
            },
            clientReferenceNumber: {
              label: 'Client Reference Number',
              placeholder: 'Client Reference Number',
              fieldName: 'clientReferenceNumber',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: (ctx: Ctx) => ctx.values.connectionType === 'Paylink',
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
            },
            SendEmail: {
              label: 'Send Email',
              fieldName: 'SendEmail',
              initValue: false,
              dataType: 'boolean',
              getInputState: () => 'optional',
              showField: () => true,
              getElement(ctx: Ctx) {
                return createCheckboxField(this, ctx);
              },
            },
            termsAndConditions: {
              label: 'Terms And Conditions',
              placeholder: 'Terms and Condition',
              fieldName: 'termsAndConditions',
              initValue: '',
              dataType: 'text',
              getInputState: () => 'optional',
              showField: () => false,
              getElement(ctx: Ctx) {
                return createTextField(this, ctx);
              },
            },
            isAllow2Banks: {
              label: 'isAllow2Banks',
              fieldName: 'isAllow2Banks',
              initValue: false,
              dataType: 'boolean',
              showField: () => false,
              getInputState: () => 'optional',
              getElement(ctx: Ctx) {
                return createToggleField(this, ctx);
              },
              validate() {
                return Yup.string().notRequired();
              },
            },
            showSave: {
              label: 'showSave',
              fieldName: 'showSave',
              initValue: false,
              dataType: 'boolean',
              showField: () => false,
              getInputState: () => 'optional',
              getElement(ctx: Ctx) {
                return createToggleField(this, ctx);
              },
              validate() {
                return Yup.string().notRequired();
              },
            },
          },
          showSection: () => true,
        },
      ],
    }),
    [],
  );

  return useMemo(() => fieldUtilsFactory<NewAccountData>(sections), []);
};

export default useAccountCreationFields;
